import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Transforms } from "slate";
import RichInputControls, {
  type TRichInputControlsItem,
  type TRichInputControlsItemProps,
} from "@atlas/uicorn/lib/Control/RichInputControls";
import styled from "styled-components";
import UiRichInput, {
  SRichInput,
  SRichInputEditable,
  TRichInputProps,
} from "@atlas/uicorn/lib/Input/RichInput";
import { controlItems } from "../../../form-rich-input";
import createEditorInstance from "@atlas/tool/box/slate/createEditorInstance";
import useLastRef from "@atlas/tool/box/hooks/useLastRef";
import focusEditor from "@atlas/tool/box/slate/focusEditor";
import withAttachments from "@atlas/tool/box/slate/withAttachments";
import useControlledState from "@atlas/tool/box/hooks/useControlledState";
import Alert from "@atlas/uicorn/lib/Data/Alert";
import ComposerAttachments from "../composer-attachments";
import AttachmentsPickerButton, {
  attachFilesHotkey,
} from "@atlas/uicorn/lib/Control/AttachmentPickerButton";
import useRefCallback from "use-ref-callback";
import uploadFiles from "@atlas/tool/box/file/uploadFiles";
import type { TAttachment } from "@atlas/tool/box/file/types";

const Composer = React.forwardRef(function Composer(
  props: TComposerProps,
  ref: React.ForwardedRef<TNewComposerRef>
) {
  const {
    className,
    placeholder = "Reply",
    actions,
    supportAttachments,
    defaultAttachments = [],
    onChangeAttachments,
    header,
    ...rest
  } = props;

  const [localError, setLocalError] = useState<string>();
  const clearLocalError = useCallback(() => setLocalError(undefined), []);

  const [uploadingAttachments, setUploadingAttachments] = useState(0);
  const [attachments, setAttachments] = useControlledState(
    undefined,
    onChangeAttachments,
    defaultAttachments
  );

  const addAttachments = useRefCallback((attachments: TAttachment[]) =>
    setAttachments((existing) => existing.concat(attachments))
  );

  const removeAttachment = useRefCallback((attachment: TAttachment) =>
    setAttachments((attachments) => attachments.filter((a) => a !== attachment))
  );

  const composerBottomControlsProps = useMemo(
    () => ({ addAttachments }),
    [addAttachments]
  );

  const editor = useMemo(() => {
    const editor = createEditorInstance({});
    return withAttachments(
      editor,
      setAttachments,
      setUploadingAttachments,
      setLocalError
    );
  }, [setAttachments, setUploadingAttachments, setLocalError]);
  const editorRef = useLastRef(editor);

  const reset = () => {
    setAttachments([]);
    editorRef.current.children.map(() => {
      Transforms.delete(editorRef.current, { at: [0] });
    });

    editorRef.current.children = [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ];
    editorRef.current.onChange();
  };

  const focus = () => {
    focusEditor(editorRef.current);
  };

  useImperativeHandle(ref, () => ({
    reset,
    focus,
    getAttachments: () => attachments,
    setAttachments,
  }));

  const handleKeyDown = useRefCallback<
    React.KeyboardEventHandler<HTMLDivElement>
  >((event) => {
    if (supportAttachments && attachFilesHotkey.matches(event)) {
      event.preventDefault();
      uploadFiles()
        .then((uploadedFiles) => {
          if (!uploadedFiles) return;

          setAttachments((existing) => existing.concat(uploadedFiles));
        })
        .catch(() => setLocalError("Failed to upload files"));
      return;
    }

    rest.onKeyDown && rest.onKeyDown(event);
  });

  return (
    <UiRichInput
      {...rest}
      editor={editor}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
    >
      {(children) => (
        <SInputContainer className={className}>
          {header && React.isValidElement(header) && (
            <SComposerHeader>{header}</SComposerHeader>
          )}
          <SRichInputControls items={controlItems} />
          {localError && (
            <SError danger message={localError} onClose={clearLocalError} />
          )}
          {children}
          <SActions>
            {supportAttachments && (
              <ComposerAttachments
                attachments={attachments}
                uploading={uploadingAttachments}
                onRemove={removeAttachment}
              />
            )}

            <SBottomActionsRow>
              {supportAttachments && (
                <RichInputControls
                  items={[attachmentsControlItem]}
                  customProps={composerBottomControlsProps}
                />
              )}
              {actions}
            </SBottomActionsRow>
          </SActions>
        </SInputContainer>
      )}
    </UiRichInput>
  );
});

export default Composer;

export const attachmentsControlItem: TRichInputControlsItem<
  TComposerControlsItemProps
> = ({
  disabled,
  addAttachments,
}: TComposerControlsItemProps): JSX.Element | null =>
  addAttachments ? (
    <AttachmentsPickerButton
      key="attachments"
      disabled={disabled}
      tabIndex={-1}
      onSelect={addAttachments}
    />
  ) : null;

export const SComposerHeader = styled.header`
  > * {
    display: flex;
    gap: var(--gap);
    padding: 0.8rem 1.2rem;
    border-bottom: 0.1rem solid ${(p) => p.theme.color.grey6};
    overflow: auto;
    ${(p) => p.theme.patch.noScroll()}
  }
`;

const SRichInputControls = styled(RichInputControls)`
  padding: var(--padding);
  border-bottom: 1px solid var(--border-color);
  width: 100%;
`;

export const SInputContainer = styled.div`
  --padding: 8px;
  --margin: 20px;
  --border-color: ${(p) => p.theme.color.grey6};

  background: ${(p) => p.theme.color.grey0};
  box-shadow: ${(p) => p.theme.shadow.low};

  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin: 0 20px 20px;

  ${SRichInput} {
    padding: var(--padding);
  }

  ${SRichInputEditable} {
    min-height: 52px !important;
    max-height: 200px;
  }
`;

const SError = styled(Alert)`
  margin: 8px;
`;

const SActions = styled.div`
  padding: 8px 12px 12px;
`;

const SBottomActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

export type TComposerAddAttachmentsHandler = (
  attachments: TAttachment[]
) => void;

type TRichInputControlsExtension = {
  addAttachments?: TComposerAddAttachmentsHandler;
};

export type TComposerControlsItemProps = TRichInputControlsItemProps &
  TRichInputControlsExtension;

export type TComposerProps = Pick<
  TRichInputProps,
  "defaultValue" | "onChange" | "onKeyDown" | "disabled"
> & {
  className?: string;
  actions?: React.ReactNode;
  placeholder?: string;
  supportAttachments?: boolean;
  defaultAttachments?: TAttachment[];
  onChangeAttachments?: (attachments: TAttachment[]) => void;
  header?: React.ReactNode;
};

export type TNewComposerRef = {
  reset: () => void;
  focus: () => void;
  getAttachments: () => TAttachment[];
  setAttachments: (attachments: TAttachment[]) => void;
};
